import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Copy, CheckCircle2, AlertCircle, Clock, Loader2, ExternalLink } from "lucide-react";

/**
 * Real-time "Setup inbound" status card.
 *
 * Shows the active company's dedicated inbound email + a live health indicator:
 *   - Green: received ≥1 email in the last 30 days
 *   - Amber: configured but no emails received (forwarding may not be set)
 *   - Red: no inbound email assigned (edge case, backfill missed)
 */
export default function InboundStatusCard({ testid = "inbound-status-card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/inbound/status")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Live refresh every 30s — cheap Mongo query.
    const t = setInterval(() => {
      api.get("/inbound/status").then(({ data }) => setData(data)).catch(() => {});
    }, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success("Email copiata");
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E8E4D9] bg-white p-5 flex items-center gap-3" data-testid={testid}>
        <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
        <span className="text-sm text-zinc-500">Verifica setup inbound…</span>
      </div>
    );
  }
  if (!data || !data.configured) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5" data-testid={testid}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900 text-sm">Email inbound non configurata</div>
            <p className="text-xs text-red-800 mt-1">Contatta il supporto per assegnare un indirizzo dedicato al cliente.</p>
          </div>
        </div>
      </div>
    );
  }

  const active = !!data.active;
  const last = data.last_received_at ? new Date(data.last_received_at) : null;
  const daysAgo = last ? Math.floor((Date.now() - last.getTime()) / 86400000) : null;
  const tone = active ? "ok" : "warn";
  const toneCls = tone === "ok"
    ? "border-emerald-200 bg-emerald-50/40"
    : "border-amber-200 bg-amber-50/60";
  const dotCls = tone === "ok" ? "bg-emerald-500" : "bg-amber-500";

  return (
    <div className={`rounded-xl border p-5 ${toneCls}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="relative shrink-0">
            <div className="w-9 h-9 rounded-lg bg-white grid place-items-center border border-[#E8E4D9]">
              <Mail className="w-4 h-4 text-[#1D4ED8]" />
            </div>
            <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${dotCls}`} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#4A4A4A] leading-none">Setup inbound</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <code className="text-xs font-mono text-[#0A0A0A] truncate max-w-[280px]" title={data.inbound_email}>
                {data.inbound_email}
              </code>
              <button
                onClick={() => copy(data.inbound_email)}
                data-testid={`${testid}-copy`}
                className="p-1 rounded hover:bg-white/70"
                title="Copia"
              >
                <Copy className="w-3 h-3 text-[#4A4A4A]" />
              </button>
            </div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          active ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
        }`}>
          {active ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {active ? "Attivo" : "Nessuna ricezione"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#737373] font-bold">Ultimi 30gg</div>
          <div className="mt-0.5 font-display font-extrabold text-lg tabular-nums text-[#0A0A0A] leading-none">
            {data.count_30d} <span className="text-[10px] font-normal text-[#737373]">fatture</span>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#737373] font-bold">Ultima ricezione</div>
          <div className="mt-0.5 text-sm font-semibold text-[#0A0A0A]">
            {last ? (daysAgo === 0 ? "oggi" : daysAgo === 1 ? "ieri" : `${daysAgo}gg fa`) : "mai"}
          </div>
        </div>
      </div>

      {data.top_senders?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E8E4D9]/60">
          <div className="text-[9px] uppercase tracking-widest text-[#737373] font-bold mb-1.5">Mittenti frequenti</div>
          <div className="flex flex-wrap gap-1.5">
            {data.top_senders.map((s) => (
              <span key={s.email} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#E8E4D9] text-[10px] font-mono text-[#0A0A0A]">
                {s.email.split("@").pop()} <span className="text-[#737373]">×{s.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {!active && (
        <a
          href="/docs#inbound-setup"
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-900"
        >
          Come configurare il forwarding da Google Ads/Meta <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}
